import * as React from "react";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedEnumerationItem: Api.CommonEnumerationItem;
  storedEnumerationItem: Api.CommonEnumerationItem;
  parentObjectId: string;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

export type EditEnumerationItemProps = StateProps & DispatchProps & OwnProps;

export type EditEnumerationItemState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsEnumeration[];
  locsEdited: Api.LocsEnumeration[];
};

const enumerationItemFields = nameofFactory<Api.CommonEnumerationItem>();

export class EditEnumerationItem extends React.Component<
  EditEnumerationItemProps,
  EditEnumerationItemState
> {
  constructor(props: EditEnumerationItemProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedEnumerationItem,
      storedEnumerationItem,
      parentObjectId,
      activeUser,
      columns,
      pdApi,
      toggleModal,
      isShortenTextEnabled
    } = this.props;
    const isNew = !storedEnumerationItem;
    const editorTitle = "enumerator";
    const deleteDisabled = isNew;
    const showLocalizationFields = pdApi.enumerations.find(
      (enumeration: Api.CommonEnumeration) => enumeration.id === parentObjectId
    )?.isLocalized;

    return (
      <Editor
        itemName={editorTitle}
        originalObject={storedEnumerationItem}
        editedObject={editedEnumerationItem}
        hasWritePermission={hasWriteAccess(activeUser)}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedEnumerationItem,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("enumerationItems");
        }}
        create={() => {
          this.props.editActions.add("enumerationItems");
        }}
        remove={{
          callback: toggleModal,
          enabledTooltip: "common enumerator",
          disabled: deleteDisabled
        }}
      >
        <NumberField
          id="value"
          label="Value"
          value={editedEnumerationItem.enumValue}
          isError={!this.isFieldValid(enumerationItemFields("enumValue"))}
          errorMessage="The value must be unique."
          onChange={enumValue =>
            this.props.editActions.setEdited({
              ...editedEnumerationItem,
              enumValue
            })
          }
          minValue=""
        />
        <TextField
          id="name"
          label="Name"
          value={editedEnumerationItem.enumName}
          isError={!this.isFieldValid(enumerationItemFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedEnumerationItem.enumName)
              ? "The name must be unique in the enumeration."
              : ""
          }
          onChange={enumName =>
            this.props.editActions.setEdited({
              ...editedEnumerationItem,
              enumName
            })
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumerationItem.comment}
          onChange={comment =>
            this.props.editActions.setEdited({
              ...editedEnumerationItem,
              comment
            })
          }
        />
        {showLocalizationFields && (
          <>
            <Divider horizontal={true}>{"Localized texts"}</Divider>
            <TextIndicatorField
              id={"needsUpdateIndicator"}
              text={"Edited texts will be translated."}
              isWarning={this.englishTranslationsChanged()}
            />
            <TextField
              id="englishName"
              label="English name"
              value={editedEnumerationItem.englishName}
              onChange={value => {
                this.handleEnglishTranslationsChange({
                  ...editedEnumerationItem,
                  englishName: value
                } as Api.CommonEnumerationItem);
              }}
              isError={!this.isFieldValid("englishName")}
            />
            <TextField
              id="description"
              label="Description"
              value={editedEnumerationItem.description}
              onChange={value => {
                this.handleEnglishTranslationsChange({
                  ...editedEnumerationItem,
                  description: value
                } as Api.CommonEnumerationItem);
              }}
              isError={!this.isFieldValid("description")}
            />
            <ButtonField
              id={"viewTranslationsButton"}
              label="View translations"
              onButtonClick={this.toggleEditLocalizationStatusModal}
            />
          </>
        )}
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Enumeration item"}
            editedItem={{ ...editedEnumerationItem, locs: this.state.locsEdited }}
            getOption="enumerationItemLocs"
            columns={columns["Enumeration Localization Status"]}
            pdApi={pdApi}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={hasWriteAccess(activeUser)}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedEnumerationItem.englishName !==
      this.props.storedEnumerationItem?.englishName &&
    this.props.editedEnumerationItem.description !== this.props.storedEnumerationItem?.description;

  private handleEnglishTranslationsChange(item: Api.CommonEnumerationItem) {
    const { storedEnumerationItem, editedEnumerationItem } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.englishName,
              description: item.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEnumeration
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.englishName,
                description: item.description
              } as Api.LocsEnumeration;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedEnumerationItem?.englishName,
              description: storedEnumerationItem?.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEnumeration
          ]
        : locsOriginal;

    this.setState((prevState: EditEnumerationItemState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedEnumerationItem,
      englishName: item.englishName,
      description: item.description
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditEnumerationItemState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsEnumeration[],
    oldLocalizations: Api.LocsEnumeration[]
  ): void => {
    const { editedEnumerationItem } = this.props;
    this.setState((prevState: EditEnumerationItemState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedEnumerationItem,
      englishName: newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text,
      description: newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")
        ?.description,
      locs: newLocalizations
    } as Api.CommonEnumerationItem);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsEnumeration[]): void => {
    this.setState((prevState: EditEnumerationItemState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };

  private isFieldValid = (field: string): boolean => {
    const { editedEnumerationItem, storedEnumerationItem, pdApi, parentObjectId } = this.props;
    const isNew = !storedEnumerationItem;
    let retval = true;
    const showLocalizationFields = pdApi.enumerations.find(
      (enumeration: Api.CommonEnumeration) => enumeration.id === parentObjectId
    )?.isLocalized;
    
    switch (field) {
      case enumerationItemFields("englishName"):
        {
          const { englishName } = editedEnumerationItem;
          return !showLocalizationFields
            ? true
            : englishName
            ? !this.isEmptyString(englishName)
            : false;
        }
        break;
      case enumerationItemFields("description"):
        {
          const { description } = editedEnumerationItem;
          return !showLocalizationFields
            ? true
            : description
            ? !this.isEmptyString(description)
            : false;
        }
        break;
      case enumerationItemFields("enumName"):
        {
          const reservedEnumNames = pdApi.enumerationItems
            .filter(x => x.enumerationId === this.props.parentObjectId)
            .map(x => x.enumName);
          const notEmpty = !this.isEmptyString(editedEnumerationItem.enumName);
          const unique = !reservedEnumNames?.includes(editedEnumerationItem.enumName);
          const unchangedEdit =
            !isNew && editedEnumerationItem.enumName === storedEnumerationItem.enumName;
          retval = reservedEnumNames ? (notEmpty && unique) || unchangedEdit : true;
        }
        break;
      case enumerationItemFields("enumValue"):
        {
          const reservedEnumValues = this.props.pdApi.enumerationItems
            .filter(x => x.enumerationId === this.props.parentObjectId)
            .map(x => x.enumValue);
          const unique = !reservedEnumValues?.includes(editedEnumerationItem.enumValue);
          const unchangedEdit =
            !isNew && editedEnumerationItem.enumValue === storedEnumerationItem.enumValue;
          retval = reservedEnumValues ? unique || unchangedEdit : true;
        }
        break;
      default:
        retval = true;
    }
    return retval;
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedEnumerationItem } = this.props;
    const temp = _.keys(editedEnumerationItem);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumerationItem: state.editView.editedObject,
  storedEnumerationItem: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEnumerationItem);
